/**
 * Redaction backstop for Sentry Logs.
 *
 * `consoleLoggingIntegration` forwards every console.warn/error argument to Sentry
 * Logs, serialized with normalize(value, 3). Strings survive that normalization at
 * any depth, so an AxiosError passed to console.error exports its `config.url`
 * (query-string access tokens) and `config.data` (request body) verbatim. The
 * `beforeSend` hook does NOT cover logs - they route through _INTERNAL_captureLog,
 * whose only hook is `beforeSendLog`.
 *
 * Call sites should still log summaries rather than raw errors (see
 * `describeErrorForLog` in errorUtils). This module is the second line of defence
 * for the ~500 console call sites that have not been audited individually.
 */

const REDACTED = "[redacted]";

/** Keys whose JSON string values must never leave the browser. */
const SENSITIVE_KEYS = [
    "password",
    "newPassword",
    "oldPassword",
    "confirmPassword",
    "token",
    "accessToken",
    "access_token",
    "refreshToken",
    "refresh_token",
    "invitationToken",
    "invitation_token",
    "anonymous_invitation_token",
    "authorization",
    "apiKey",
    "api_key",
    "secret",
    "otp",
    // Test-taker / student PII captured on assessment entry forms.
    "email",
    "testTakerEmail",
    "testTakerName",
    "registerNumber",
    "parentName",
    "childAge",
    "gender",
    "dateOfBirth",
    "phone",
    "phoneNumber",
];

/** Query params that carry bearer-equivalent credentials. */
const SENSITIVE_QUERY_PARAMS = [
    "token",
    "access_token",
    "accessToken",
    "invitationToken",
    "invitation_token",
    "apiKey",
    "api_key",
    "password",
];

const jsonFieldPattern = new RegExp(
    `("(?:${SENSITIVE_KEYS.join("|")})"\\s*:\\s*)"(?:[^"\\\\]|\\\\.)*"`,
    "gi"
);

const queryParamPattern = new RegExp(
    `([?&](?:${SENSITIVE_QUERY_PARAMS.join("|")})=)[^&\\s"'\\\\]+`,
    "gi"
);

const bearerPattern = /\bBearer\s+[A-Za-z0-9._~+/=-]{8,}/gi;

const emailPattern = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

/**
 * Strips credentials and PII from a log message.
 * Safe to call on any string; returns the input unchanged when nothing matches.
 */
export const scrubSentryLogMessage = (message: string): string => {
    if (!message) return message;

    return message
        .replace(jsonFieldPattern, `$1"${REDACTED}"`)
        .replace(queryParamPattern, `$1${REDACTED}`)
        .replace(bearerPattern, `Bearer ${REDACTED}`)
        .replace(emailPattern, "[redacted-email]");
};

type SentryLogLike = {
    message?: unknown;
    attributes?: Record<string, unknown>;
};

/**
 * `beforeSendLog` hook: scrubs the rendered message and any string attributes.
 * Returns the log so it is still delivered - only the sensitive spans are removed.
 */
export const scrubSentryLog = <T extends SentryLogLike>(log: T): T => {
    if (!log) return log;

    if (typeof log.message === "string") {
        log.message = scrubSentryLogMessage(log.message);
    }

    if (log.attributes && typeof log.attributes === "object") {
        for (const [key, value] of Object.entries(log.attributes)) {
            if (typeof value === "string") {
                log.attributes[key] = scrubSentryLogMessage(value);
            }
        }
    }

    return log;
};
